// src/phoneupload/index.js — „Качи от телефона" (ПРОМТ-37, Макс 29.10.2026).
//
// Прост екран за телефон: снимка (или PDF) на покупна фактура отива направо в
// „Доставки". Без редакция на редовете — документът остава „за преглед" и
// кантората го проверява на компютъра. Клиентът вижда само този екран и
// своите качени файлове.
//
// Няколко снимки = няколко страници на ЕДНА фактура: сглобяват се в един PDF
// на телефона (src/shared/pagesToPdf.js), защото сървърът приема един файл на
// документ. „Опитай пак" праща СЪЩИЯ файл — ако първият опит все пак е
// стигнал, сървърът го познава по sha256 и не прави втора доставка.

import { el, clear } from '../shared/dom.js';
import { api } from '../shared/api.js';
import { state, activeCompanyObj, cq, canEditPerm } from '../shared/state.js';
import { panelH2, loadError } from '../shared/ui.js';
import { uploadRawProgress, CLIENT_DONE_TEXT } from '../shared/upload.js';
import { pagesToPdf } from '../shared/pagesToPdf.js';
import { dfmt } from '../shared/format.js';

// Най-дългата страна на снимката. Снимка от телефон е 4000 px и 3–5 MB; на
// 2400 px текстът на фактурата се чете ясно, а качването по мобилни данни е
// няколко пъти по-кратко (и няколко страници се побират в лимита от 20 MB).
const MAX_SIDE = 2400;

// Текстът след успешно качване за кантората — дословно от ПРОМТ-37 (и в
// „Как да…?"). Клиентът вижда CLIENT_DONE_TEXT (ПРОМТ-41, Д6).
const DONE_TEXT = 'Готово — качено, ще се обработи';

const STATUS_TEXT = {
  uploaded: 'Качен — чака да бъде разчетен',
  ocr_review: 'При кантората за преглед',
  confirmed: 'Прегледан и потвърден',
};

const isImage = (f) => /^image\//.test(f.type || '') || /\.(jpe?g|png|heic|heif|webp)$/i.test(f.name || '');
const isPdf = (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '');

// Снимка → JPEG до MAX_SIDE. createImageBitmap завърта по EXIF (снимка,
// направена с обърнат телефон, не излиза легнала).
async function toJpeg(file) {
  if (typeof createImageBitmap === 'function' && typeof document !== 'undefined') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const k = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bmp.width * k);
      canvas.height = Math.round(bmp.height * k);
      canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
      if (bmp.close) bmp.close();
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
      if (blob) return new Uint8Array(await blob.arrayBuffer());
    } catch (e) { /* по-долу: оригиналът, ако е JPEG */ }
  }
  if (file.type === 'image/jpeg' || /\.jpe?g$/i.test(file.name || '')) return new Uint8Array(await file.arrayBuffer());
  throw new Error('Снимката „' + file.name + '“ не може да се прочете. Снимайте я пак или изберете JPG.');
}

const baseName = (n) => String(n || 'Снимка').replace(/\.[^.]+$/, '');

// Какво ще се качи: PDF-ите — всеки отделен документ; снимките — един
// документ с N страници (или всяка отделно, ако човекът е отметнал така).
export function planJobs(files, eachSeparate) {
  const pdfs = files.filter(isPdf);
  const imgs = files.filter((f) => !isPdf(f) && isImage(f));
  const jobs = pdfs.map((f) => ({ kind: 'pdf', files: [f], name: f.name }));
  if (imgs.length === 1 || (imgs.length && eachSeparate)) {
    imgs.forEach((f) => jobs.push({ kind: 'image', files: [f], name: baseName(f.name) + '.jpg' }));
  } else if (imgs.length > 1) {
    jobs.push({ kind: 'pages', files: imgs, name: baseName(imgs[0].name) + ' (' + imgs.length + ' стр.).pdf' });
  }
  return jobs;
}

// Файлът за изпращане — сглобява се ВЕДНЪЖ и се пази в задачата, за да е
// „Опитай пак" байт по байт същият.
async function buildBlob(job) {
  if (job.kind === 'pdf') return job.files[0];
  if (job.kind === 'image') return new Blob([await toJpeg(job.files[0])], { type: 'image/jpeg' });
  const pages = [];
  for (const f of job.files) pages.push(await toJpeg(f));
  return new Blob([pagesToPdf(pages)], { type: 'application/pdf' });
}

export function view(page) {
  clear(page);
  const st = state();
  const company = activeCompanyObj();
  if (!company) { window.CRM.needsCompany(page, 'Изберете фирмата, за която качвате фактурата.'); return; }
  const isClient = !!(st.user && st.user.role === 'client');
  const canUpload = canEditPerm('purchases');

  const panel = el('div', { class: 'panel' });
  panel.appendChild(el('div', { class: 'panel-head' }, ...panelH2('Качи фактура от телефона')));
  const body = el('div', { style: 'padding:16px 18px' });
  panel.appendChild(body);
  page.appendChild(panel);

  body.appendChild(el('p', { class: 'pu-intro', text: 'Снимайте покупната фактура (от доставчик) или изберете готов PDF. Ако фактурата е на няколко листа, снимайте всеки лист — те стават един документ. Кантората ще я прегледа; тук нищо не се попълва на ръка.' }));
  if (!canUpload) {
    body.appendChild(el('p', { class: 'pu-intro', text: 'Имате право само да разглеждате доставките — качване не е позволено. Помолете кантората за право „Доставки“.' }));
  }

  // Избраните, но още неизпратени файлове.
  let pending = [];
  const pagesList = el('ul', { class: 'pu-pages', 'aria-label': 'Избрани за качване' });
  const splitBox = el('input', { type: 'checkbox', id: 'pu-split' });
  const splitRow = el('div', { class: 'pu-split' }, splitBox,
    el('label', { for: 'pu-split', text: 'Всяка снимка е отделна фактура' }));
  const sendBtn = el('button', { class: 'btn pu-send', type: 'button', text: 'Качи' });
  const jobsBox = el('div', { class: 'pu-jobs', 'aria-live': 'polite' });

  function addFiles(list) {
    const arr = [...(list || [])].filter((f) => isImage(f) || isPdf(f));
    const bad = [...(list || [])].length - arr.length;
    pending = pending.concat(arr);
    drawPending(bad ? 'Пропуснат(и): ' + bad + ' — приемат се само снимки и PDF.' : '');
  }

  function drawPending(note) {
    clear(pagesList);
    const imgs = pending.filter((f) => !isPdf(f));
    let pageNo = 0;
    pending.forEach((f, i) => {
      const img = !isPdf(f);
      if (img) pageNo++;
      let thumb = null;
      if (img && typeof URL !== 'undefined' && URL.createObjectURL) {
        try { thumb = el('img', { src: URL.createObjectURL(f), alt: '' }); } catch (e) { thumb = null; }
      }
      const what = img ? (imgs.length > 1 && !splitBox.checked ? 'Страница ' + pageNo : 'Снимка') : 'PDF';
      pagesList.appendChild(el('li', {},
        thumb || el('span', { 'aria-hidden': 'true', style: 'width:44px;text-align:center', text: img ? '▣' : 'PDF' }),
        el('span', { class: 'pu-name', title: f.name, text: what + ' — ' + f.name }),
        el('button', { class: 'btn sm ghost', type: 'button', text: 'Махни', 'aria-label': 'Махни ' + what + ' — ' + f.name,
          onClick: () => { pending.splice(i, 1); drawPending(''); } })));
    });
    splitRow.style.display = imgs.length > 1 ? '' : 'none';
    const jobs = planJobs(pending, splitBox.checked);
    sendBtn.disabled = !pending.length || !canUpload;
    sendBtn.textContent = !pending.length ? 'Качи' : jobs.length === 1
      ? (jobs[0].kind === 'pages' ? 'Качи фактурата (' + jobs[0].files.length + ' страници)' : 'Качи фактурата')
      : 'Качи ' + jobs.length + ' фактури';
    noteLine.textContent = note || '';
  }
  splitBox.addEventListener('change', () => drawPending(''));
  const noteLine = el('p', { class: 'pu-job-msg', role: 'status' });

  // Полето е точно по ПРОМТ-37: камерата отзад, снимки и PDF, няколко. На
  // Android capture отваря направо камерата — затова има и второ поле без
  // него, за готов файл.
  const camInput = el('input', { type: 'file', id: 'pu-camera', class: 'sr-only', accept: 'image/*,application/pdf', capture: 'environment', multiple: 'multiple' });
  const fileInput = el('input', { type: 'file', id: 'pu-files', class: 'sr-only', accept: 'image/*,application/pdf', multiple: 'multiple' });
  [camInput, fileInput].forEach((inp) => inp.addEventListener('change', () => { addFiles(inp.files); inp.value = ''; }));
  const keyOpen = (inp) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inp.click(); } };
  if (canUpload) {
    body.appendChild(el('div', { class: 'pu-pick' },
      el('label', { class: 'btn pu-camera', for: 'pu-camera', tabindex: '0', role: 'button', text: 'Снимай / избери файл', onKeydown: keyOpen(camInput) }),
      camInput,
      el('label', { class: 'btn ghost', for: 'pu-files', tabindex: '0', role: 'button', text: 'Избери готов файл (PDF или снимка)', onKeydown: keyOpen(fileInput) }),
      fileInput));
    body.appendChild(pagesList);
    body.appendChild(splitRow);
    body.appendChild(noteLine);
    body.appendChild(sendBtn);
  }
  body.appendChild(jobsBox);
  drawPending('');

  // Една задача = един документ: файлът, лентата, съобщението, „Опитай пак".
  function jobRow(job) {
    const fill = el('span');
    const bar = el('div', { class: 'pu-bar', role: 'progressbar', 'aria-label': 'Качване на ' + job.name, 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': '0' }, fill);
    const msg = el('div', { class: 'pu-job-msg', text: 'Чака…' });
    const retry = el('button', { class: 'btn sm', type: 'button', text: 'Опитай пак', style: 'display:none', onClick: () => run(job) });
    const box = el('div', { class: 'pu-job' }, el('div', { class: 'pu-job-name', text: job.name }), bar, msg, retry);
    job.ui = {
      progress(p) { const v = Math.round(p * 100); fill.style.width = v + '%'; bar.setAttribute('aria-valuenow', String(v)); },
      set(kind, text) { box.classList.toggle('done', kind === 'done'); box.classList.toggle('failed', kind === 'failed'); msg.textContent = text; retry.style.display = kind === 'failed' ? '' : 'none'; },
    };
    return box;
  }

  async function run(job) {
    job.ui.set('sending', job.blob ? 'Качва се…' : 'Подготвя се…');
    job.ui.progress(0);
    try {
      if (!job.blob) job.blob = await buildBlob(job);
      job.ui.set('sending', 'Качва се…');
      const r = await uploadRawProgress('/api/purchases/upload' + cq(), job.blob, job.name, (p) => job.ui.progress(p));
      const no = r && r.doc ? (r.doc.number || r.doc.id) : '';
      job.ui.set('done', r && r.duplicate
        ? 'Готово — този файл вече е качен' + (no ? ' (доставка № ' + no + ')' : '') + '. Не е добавен втори път.'
        : isClient ? CLIENT_DONE_TEXT : DONE_TEXT + '. Кантората ще прегледа фактурата.');
      loadMine();
    } catch (e) {
      job.ui.set('failed', e.message || 'Файлът не се качи.');
    }
  }

  sendBtn.addEventListener('click', async () => {
    const jobs = planJobs(pending, splitBox.checked);
    if (!jobs.length) return;
    pending = [];
    drawPending('');
    jobs.forEach((j) => jobsBox.appendChild(jobRow(j)));
    // Една по една: на мобилни данни паралелните качвания се пречат.
    for (const j of jobs) await run(j);
  });

  // Моите качени файлове — само качените от този човек.
  const mine = el('div', { class: 'pu-mine' });
  body.appendChild(mine);
  async function loadMine() {
    let docs;
    try { docs = await api('/purchases' + cq()); } catch (e) {
      clear(mine); mine.appendChild(loadError(loadMine, 'Списъкът с качените файлове не се зареди. Проверете връзката.')); return;
    }
    const me = st.user && st.user.id;
    const own = (Array.isArray(docs) ? docs : []).filter((d) => d.created_by === me && d.company_id === company.id)
      .sort((a, b) => Number(b.id) - Number(a.id)).slice(0, 30);
    clear(mine);
    mine.appendChild(el('h3', { text: 'Моите качени файлове' }));
    if (!own.length) { mine.appendChild(el('p', { class: 'pu-job-msg', text: 'Още нищо не сте качили за тази фирма.' })); return; }
    mine.appendChild(el('ul', {}, ...own.map((d) => el('li', {},
      el('span', { class: 'pu-name', text: d.file_name || ('Документ ' + d.id) }),
      el('span', { class: 'pu-when', text: dfmt(String(d.created_at || '').slice(0, 10)) + ' · ' + (STATUS_TEXT[d.status] || 'При кантората') })))));
  }
  loadMine();

  // Кантората може да отвори и целия списък; клиентът вижда само този екран.
  if (!isClient) {
    body.appendChild(el('button', { class: 'btn ghost', type: 'button', style: 'margin-top:14px', text: 'Към всички доставки',
      onClick: () => { st.view = 'purchases'; window.CRM.renderShell(); } }));
  }
}
